import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addCareRoundTypeFailure,
  addCareRoundTypeSuccess,
  deleteCareRoundTypeFailure,
  deleteCareRoundTypeSuccess,
  editCareRoundTypeFailure,
  editCareRoundTypeSuccess,
  fetchCareRoundTypeFailure,
  fetchCareRoundTypeItemFailure,
  fetchCareRoundTypeItemSuccess,
  fetchCareRoundTypeSuccess,
  searchCareRoundTypeFailure,
  searchCareRoundTypeSuccess,
} from "./actions";
import {
  ADD_CARE_ROUND_TYPE,
  DELETE_CARE_ROUND_TYPE,
  EDIT_CARE_ROUND_TYPE,
  FETCH_CARE_ROUND_TYPE,
  FETCH_CARE_ROUND_TYPE_ITEM,
  SEARCH_CARE_ROUND_TYPE,
} from "./constants";

const SETTINGS_PATH = "/d/resident/care-round-types";

const normalizeCareRoundTypeSearchList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export function* searchCareRoundTypeSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const locationId = payload?.location_id;
  if (!locationId) {
    yield put(searchCareRoundTypeSuccess([]));
    return;
  }
  const params = { location_id: locationId };
  if (payload?.search_term && String(payload.search_term).trim()) {
    params.search_term = String(payload.search_term).trim();
  }
  const query = QueryString.stringify(params);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/round-type/search?${query}`,
    );
    yield put(
      searchCareRoundTypeSuccess(normalizeCareRoundTypeSearchList(data)),
    );
  } catch (e) {
    yield put(searchCareRoundTypeFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to search care round types"));
  }
}

export function* fetchCareRoundTypeSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = payload ? QueryString.stringify(payload) : "";
  try {
    const { data } = yield call(api(token).get, `/api/round-type?${query}`);
    const result = Array.isArray(data)
      ? { data, total: data.length, current_page: payload?.page ?? 1 }
      : { ...data, current_page: payload?.page ?? data?.current_page ?? 1 };
    yield put(fetchCareRoundTypeSuccess(result));
  } catch (e) {
    yield put(fetchCareRoundTypeFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care round types"));
  }
}

export function* addCareRoundTypeSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, onSuccess } = payload || {};
  try {
    const response = yield call(api(token).post, `/api/round-type/add`, data);
    if (response) {
      yield put(addCareRoundTypeSuccess());
      yield put(successAlert("Care round type created successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(addCareRoundTypeFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to create care round type"));
  }
}

export function* fetchCareRoundTypeItemSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id =
    typeof payload === "string" ? payload : (payload?.uuid ?? payload?.id);
  try {
    const { data } = yield call(api(token).get, `/api/round-type/${id}`);
    yield put(fetchCareRoundTypeItemSuccess(data));
  } catch (e) {
    yield put(fetchCareRoundTypeItemFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to fetch care round type"));
  }
}

export function* editCareRoundTypeSaga({ payload }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { data, history, id, uuid, onSuccess } = payload || {};
  const itemId = id ?? uuid;
  try {
    const response = yield call(
      api(token).post,
      `/api/round-type/edit/${itemId}`,
      data,
    );
    if (response) {
      yield put(editCareRoundTypeSuccess());
      yield put(successAlert("Care round type updated successfully"));
      if (typeof onSuccess === "function") onSuccess();
      else if (history) history.push(SETTINGS_PATH);
    }
  } catch (e) {
    yield put(editCareRoundTypeFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to update care round type"));
  }
}

export function* deleteCareRoundTypeSaga({ payload, onDelete }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const id = payload?.id ?? payload?.uuid;
  try {
    const data = yield call(api(token).delete, `/api/round-type/${id}`);
    if (data) {
      yield put(deleteCareRoundTypeSuccess());
      yield put(successAlert("Care round type deleted successfully"));
      typeof onDelete === "function" && onDelete();
    }
  } catch (e) {
    yield put(deleteCareRoundTypeFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e?.error === "string") yield put(errorAlert(e.error));
    else yield put(errorAlert("Failed to delete care round type"));
  }
}

export default function* CareRoundTypeSaga() {
  yield all([
    takeEvery(SEARCH_CARE_ROUND_TYPE, searchCareRoundTypeSaga),
    takeEvery(FETCH_CARE_ROUND_TYPE, fetchCareRoundTypeSaga),
    takeEvery(ADD_CARE_ROUND_TYPE, addCareRoundTypeSaga),
    takeEvery(FETCH_CARE_ROUND_TYPE_ITEM, fetchCareRoundTypeItemSaga),
    takeEvery(EDIT_CARE_ROUND_TYPE, editCareRoundTypeSaga),
    takeEvery(DELETE_CARE_ROUND_TYPE, deleteCareRoundTypeSaga),
  ]);
}
